import { useQuery } from "@tanstack/react-query";
import { ShipTier } from "@voidex/contracts-ts";
import { querySubgraph } from "../web3/subgraph";

export interface ActiveFarmSession {
	sessionId: bigint;
	shipTokenId: bigint;
	shipTier: ShipTier;
	startedAtBlock: bigint;
	startedTimestamp: bigint;
	zone: number;
	farmDuration: bigint;
	vrfDone: boolean;
	claimed: boolean;
}

interface SubgraphFarmSession {
	id: string;
	shipTokenId: string;
	ship: { tier: "Probe" | "Frigate" | "Dreadnought" };
	startedAt: string;
	startedTimestamp: string;
	zone: number;
	farmDuration: string;
	vrfDone: boolean;
	claimed: boolean;
}

interface FarmSessionQueryResult {
	farmSessions: SubgraphFarmSession[];
}

const tierMap: Record<string, ShipTier> = {
	Probe: ShipTier.Probe,
	Frigate: ShipTier.Frigate,
	Dreadnought: ShipTier.Dreadnought,
};

/**
 * Fetches active (unresolved) farm sessions for `address` from the subgraph.
 */
export function useActiveFarmSessions(address?: `0x${string}`) {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["activeFarmSessions", address],
		queryFn: async (): Promise<ActiveFarmSession[]> => {
			if (!address) return [];

			const farmerId = address.toLowerCase();
			const result = await querySubgraph<FarmSessionQueryResult>(
				`query ActiveSessions($farmer: String!) {
					farmSessions(
						where: { farmer: $farmer, resolved: false }
						orderBy: startedAt
						orderDirection: desc
						first: 100
					) {
						id
						shipTokenId
						ship { tier }
						startedAt
						startedTimestamp
						zone
						farmDuration
						vrfDone
						claimed
					}
				}`,
				{ farmer: farmerId },
			);

			return result.farmSessions.map((s) => ({
				sessionId: BigInt(s.id),
				shipTokenId: BigInt(s.shipTokenId),
				shipTier: tierMap[s.ship.tier] ?? ShipTier.Probe,
				startedAtBlock: BigInt(s.startedAt),
				startedTimestamp: BigInt(s.startedTimestamp),
				zone: s.zone,
				farmDuration: BigInt(s.farmDuration),
				vrfDone: s.vrfDone,
				claimed: s.claimed,
			}));
		},
		enabled: !!address,
		staleTime: 10_000,
		refetchInterval: 15_000,
	});

	const activeSessions = data ?? [];
	const farmingShipIds = new Set(activeSessions.map((s) => s.shipTokenId));

	return {
		activeSessions,
		farmingShipIds,
		isLoading,
		error,
		refetch,
	};
}
